"use client";
import { Check } from "lucide-react";
import ProduktSlider from "@/components/Produkte/ProduktSlider";
import Image from "next/image";
import KundenRezensionenCarousel from "@/components/KundenrezensionenCarousel";
import ImageText from "@/components/ImageText";
import ImageCard from "@/components/ImageCard";
import Section from "@/components/Section";
import PricingSection from "@/components/PricingSection";
import SpecialFeaturesOverview from "@/components/SpecialFeaturesOverview";
import FaqAccordion from "@/components/FaqAccordion";
import ContactForm from "@/components/ContactForm";

export default function Home() {
  return (
    <>
      <Section variant="gray1" className="pt-4 pb-10 sm:pt-10 sm:pb-14">
        <ProduktSlider />
      </Section>

      <Section variant="white">
        <h2 className="text-center text-4xl">Branchenlösungen</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          <ImageCard
            imageSrc="/index/products/modeprofi.webp"
            imageAlt="Dein Kassensystem von SDI: So flexibel wie Du selbst."
            title="Modeprofi - Warenwirtschaft"
            description="MODE PROFI - umfassendes Warenwirtschaftssystem für Mode- und Textilunternehmen"
            linkText="Mehr erfahren"
            linkUrl="/modeprofi"
          />
          <ImageCard
            imageSrc="/index/products/Cafe.webp"
            imageAlt="Kassensystem Einzelhandel"
            title="Cashtex - Kassensystem"
            description="Cashtex ist die moderne Komplettlösung für Kasse und Warenwirtschaft im Einzelhandel."
            linkText="Mehr erfahren"
            linkUrl="/kassensysteme/textil"
          />
          <ImageCard
            imageSrc="/industries/messee.webp"
            imageAlt="Dein Kassensystem von SDI: So flexibel wie Du selbst."
            title="Office4Trade - Auftragserfassung"
            description="Office4Trade digitalisiert die mobile Auftragserfassung auf Messen und im Außendienst: schnell, einfach, ohne Papier."
            linkText="Mehr erfahren"
            linkUrl="/office4trade"
          />
        </div>
      </Section>

      <Section variant="gray1">
        <SpecialFeaturesOverview />
      </Section>

      <Section variant="white">
        <PricingSection />
      </Section>

      {/* SECTION: Jetzt durchstarten */}
      <Section variant="dark">
        <h2 className="text-center text-4xl mb-6">
          Jetzt durchstarten – mit dem starken <br /> Kassensystem von SDI!
        </h2>
        <h3 className="text-center text-xl">
          Lass Dich beraten oder starte mit unserer Demo Version.
        </h3>

        <div className="flex justify-center mt-10">
          <a
            className="font-display text-center font-medium bg-white text-primary border border-primary
                       w-full sm:w-auto py-3 px-5 lg:py-4 lg:px-8 hover:shadow-xl cursor-pointer rounded-full transition-all hover:-translate-y-2"
            href="/support"
          >
            Unverbindlich anfragen
          </a>
        </div>
      </Section>

      <Section variant="white">
        <h2 className="text-center text-4xl mb-6">Das sagen unsere Kunden:</h2>
        <div className="mt-10">
          <KundenRezensionenCarousel />
        </div>
      </Section>

      {/* SECTION: ImageText Links */}
      <Section variant="gray1" id="kassensystem-sdi">
        <ImageText
          imagePosition="left"
          imageSrc="/kasseTouch-2.webp"
          imageAlt="Dein Kassensystem von SDI: So flexibel wie Du selbst."
          title="Dein Kassensystem von SDI: So flexibel wie Du selbst."
        >
          <p>
            Cashtex passt überall: Unser Kassensystem-Abo hat alles, was Du im
            Einzelhandel oder als Dienstleister brauchst – standortunabhängig
            und mobil. Und mit unserem Gastro-Paket erweiterst Du diese Basis
            ganz einfach mit allen wichtigen Funktionen für die Gastro mit
            Tischbedienung!
          </p>
          <ul className="ps-8">
            <li>
              <Check className="inline -ms-8 text-brand-primary" />{" "}
              <strong>Ob Handel oder Dienstleister:</strong> Wir sind das
              Kassensystem für alle Branchen
            </li>
            <li>
              <Check className="inline -ms-8 text-brand-primary" />{" "}
              <strong>Kassensystem-Abo:</strong> Perfekt für Einzelhandel und
              Dienstleister, vor Ort und unterwegs
            </li>
            <li>
              <Check className="inline -ms-8 text-brand-primary" />{" "}
              <strong>Gastro-Paket:</strong> Ideal für alle Restaurants, Cafés
              und Bars mit Tischbedienung
            </li>
          </ul>
          <div className="flex justify-center lg:justify-start">
            <a
              className="font-display text-center font-medium bg-primary text-primary-foreground border border-primary
                         w-full sm:w-auto py-3 px-5 lg:py-4 lg:px-8 hover:shadow-xl cursor-pointer rounded-full transition-all hover:-translate-y-2
                         hover:bg-brand-primary-dark mt-10"
              href="/kassensysteme/textil"
            >
              Zu den Cashtex Basis-Funktionen
            </a>
          </div>
        </ImageText>
      </Section>

      {/* SECTION: ImageText Rechts */}
      <Section variant="white">
        <ImageText
          imagePosition="right"
          imageSrc="/ECK.webp"
          imageAlt="Softwareentwicklung"
          title="1, 2, 3 SDI: So geht Kartenzahlung"
          imageClassName="object-cover"
        >
          <p>
            Cashtex ist die perfekte Ergänzung für dein Business: Mit dem
            Kartenzahlungsdienst kannst Du über unserem Kassensystem und dem EC
            Terminal ganz einfach bargeldlos kassieren. Akzeptiere alle Karten
            und mobile Payment mit nur 1,23% Gebühr für alle Zahlungsarten –
            einfach, schnell und zuverlässig.
          </p>
          <ul className="ps-8">
            <li>
              <Check className="inline -ms-8 text-brand-primary" />{" "}
              <strong>Ohne Grundgebühr</strong> oder Mindestumsatz
            </li>
            <li>
              <Check className="inline -ms-8 text-brand-primary" />{" "}
              <strong>Alle gängigen Karten und mobile Payments</strong>
            </li>
            <li>
              <Check className="inline -ms-8 text-brand-primary" /> Bis zu{" "}
              <strong>8 Stunden Akkulaufzeit</strong>
            </li>
          </ul>
          <div className="flex justify-center lg:justify-start">
            <a
              className="font-display text-center font-medium bg-primary text-primary-foreground border border-primary 
                         w-full sm:w-auto py-3 px-5 lg:py-4 lg:px-8 hover:shadow-xl cursor-pointer rounded-full transition-all hover:-translate-y-2 hover:bg-brand-primary-dark mt-10"
              href="/kassensysteme/textil"
            >
              Kartenzahlung mit Cashtex
            </a>
          </div>
        </ImageText>

        <p className="mt-20 px-10">
          <strong>Warum Cashtex besonders günstig für Dich ist:</strong>{" "}
          Normalerweise setzen sich die Gebühren für Kartenzahlung aus einer
          fixen Transaktionsgebühr (z.B. 0,09 €) und einem prozentuellen Disagio
          zusammen, wobei für Letzteres unterschiedliche Prozentsätze bei
          Debitkarte und Kreditkarte gelten. Du zahlst also bei jeder
          Kartenzahlung doppelt!
        </p>
        <p className="mb-20 mt-5 px-10">
          <strong>Mit Cashtex zahlst Du nur einmal. </strong> Und nur einen
          prozentualen Anteil – keine fixe Gebühr. Das bedeutet, dass Du gerade
          für kleine Summen auch nur wenig Gebühr zahlst. Und bei großen
          Beträgen lohnt sich die günstige Transaktionsgebühr von nur 1,23% erst
          recht!
        </p>

        <div className="payment-logos flex flex-wrap justify-evenly gap-x-7 sm:gap-x-5 gap-y-4">
          <Image
            src="/visa.svg"
            alt="VISA"
            width={32}
            height={32}
            className="h-8 w-auto"
          />
          <Image
            src="/vpay.svg"
            alt="V PAY"
            width={32}
            height={32}
            className="h-8 w-auto"
          />
          <Image
            src="/mastercard.svg"
            alt="Mastercard"
            width={32}
            height={32}
            className="h-8 w-auto"
          />
          <Image
            src="/maestro.svg"
            alt="Maestro"
            width={32}
            height={32}
            className="h-8 w-auto"
          />
          <Image
            src="/amex.svg"
            alt="American Express"
            width={32}
            height={32}
            className="h-8 w-auto"
          />
          <Image
            src="/googlepay.svg"
            alt="Google Pay"
            width={32}
            height={32}
            className="h-8 w-auto"
          />
          <Image
            src="/applepay.svg"
            alt="Apple Pay"
            width={32}
            height={32}
            className="h-8 w-auto"
          />
        </div>
      </Section>

      {/* SECTION: TSE */}
      <Section variant="gray1" className="tse-section">
        <ImageText
          imagePosition="left"
          imageSrc="/tse.webp"
          imageAlt="Softwareentwicklung"
          title="TSE & Rechtssicherheit: Wir machen es einfach!"
        >
          <p>
            TSE-Pflicht oder neue Anforderungen vom Finanzamt? Kein Problem –
            wir kennen uns aus und übernehmen die Umsetzung der Technischen
            Sicherheitseinrichtung für Dich und informieren Dich über alle
            wichtigen Änderungen.
          </p>
          <ul className="ps-8">
            <li>
              <Check className="inline -ms-8 text-brand-primary" />{" "}
              <strong>Kein zusätzlicher Aufwand:</strong> Regelmäßige und
              kostenlose Updates
            </li>
            <li>
              <Check className="inline -ms-8 text-brand-primary " />{" "}
              <strong>Rechtssicher:</strong> TSE-konform – erfüllt KassenSichV
              und GoBD
            </li>
            <li>
              <Check className="inline -ms-8 text-brand-primary" />{" "}
              <strong>Zuverlässig:</strong> Datensicherheit auf deutschen
              Servern und nach höchsten Standards
            </li>
          </ul>
          <p>
            Mit der Kassensoftware von SDI arbeitest Du immer 100%
            finanzamtkonform und bist automatisch auf der sicheren Seite.
          </p>
        </ImageText>
      </Section>

      {/* SECTION: Beratung */}
      <Section variant="dark">
        <h2 className="text-center text-4xl mb-6">
          Jetzt beraten lassen – für Deinen Erfolg!
        </h2>
        <h3 className="text-center text-xl">
          Unsere Experten beantworten gern alle Deine Fragen im persönlichen
          <br /> Beratungsgespräch.
        </h3>

        <div className="flex justify-center mt-10">
          <a
            className="font-display text-center font-medium bg-white text-primary border border-primary
                       w-full sm:w-auto py-3 px-5 lg:py-4 lg:px-8 hover:shadow-xl cursor-pointer rounded-full transition-all hover:-translate-y-2"
            href="/support"
          >
            Unverbindlich anfragen
          </a>
        </div>
      </Section>

      {/* FAQ & Contact */}
      <Section variant="white">
        <h2 className="text-center text-xl sm:text-2xl font-semibold tracking-tight text-slate-900 mb-2">
          FAQ: Alle Antworten zum Kassensystem
        </h2>
        <FaqAccordion
          faqs={[
            {
              q: "Welche Kassensysteme sind in Deutschland gesetzlich erlaubt?",
              a: "Neben elektronischen Kassensystemen sind in Deutschland auch noch sogenannte offene Ladenkassen zulässig. Diese sind mit einem hohen manuellen Aufwand verbunden, um die vorgeschriebenen, allgemeinen Aufzeichnungs- und Aufbewahrungspflichten gemäß den Grundsätzen ordnungsmäßiger Buchführung (GoBD) zu erfüllen. Für elektronische Kassensysteme gelten die Anforderungen der Kassensicherungsverordnung (KassenSichV), welche vor allem die Integration einer zertifizierten Technischen Sicherheitseinrichtung (TSE) vorschreibt. Grundsätzlich sind alle elektronischen Kassensysteme zulässig, die diese Anforderungen erfüllen. Dazu gehören: POS-Systeme, PC-Kassen, mobile Kassensysteme und moderne cloudbasierte Kassensysteme wie auch SDI sie anbietet.",
            },
            {
              q: "Gibt es in Deutschland eine elektronische Registrierkassenpflicht?",
              a: "Nein, in Deutschland besteht derzeit noch keine gesetzliche Pflicht zur Nutzung einer elektronischen Registrierkasse. Unternehmen haben die Wahl, ob sie eine elektronische Kasse oder eine offene Ladenkasse verwenden möchten. Doch obwohl aktuell keine Pflicht besteht, ist im Koalitionsvertrag der CDU und SPD eine mögliche Registrierkassenpflicht ab dem 1. Januar 2027 vorgesehen. Deren Ziel ist es, Steuerbetrug zu bekämpfen und die Steuererhebung zu vereinfachen.",
            },
            {
              q: "Welche gesetzlichen Anforderungen gelten für Kassensysteme in Deutschland?",
              a: "Die Kassensicherungsverordnung (KassenSichV) regelt die Anforderungen an elektronische Kassensysteme in Deutschland. Sie soll Manipulationen an digitalen Kassendaten verhindern und eine ordnungsgemäße, unveränderbare und nachvollziehbare Erfassung aller Kassenvorgänge sicherstellen. Die Verordnung ist seit dem 1. Januar 2020 in Kraft und umfasst: TSE-Pflicht (zertifizierte Technische Sicherheitseinrichtung), Belegausgabepflicht (gedruckt oder digital), Meldepflicht beim Finanzamt seit 1. Januar 2025, Aufbewahrungspflichten nach GoBD (10 Jahre) sowie DSFinV-K für standardisierte Kassenprüfungen.",
            },
            {
              q: "Wann brauche ich ein TSE-zertifiziertes Kassensystem?",
              a: "Ein TSE-zertifiziertes Kassensystem brauchst du immer dann, wenn Du in Deutschland ein elektronisches Kassensystem oder eine Registrierkasse mit elektronischer Aufzeichnung für Deine Barumsätze, EC- oder Kartenzahlungen verwendest – das ist seit dem 1. Januar 2020 gesetzlich vorgeschrieben. Ohne TSE geht es nur, wenn Du eine offene Ladenkasse ohne elektronische Aufzeichnung nutzt oder niemals Bargeld annimmst und kein Kassensystem verwendest.",
            },
            {
              q: "Was kostet ein Kassensystem in Deutschland?",
              a: "Die Kosten variieren stark je nach Branche, Funktionsumfang und Anbieter. Einfache Kassensysteme kosten 10–40 € pro Monat plus 100–500 € Hardware. Mittlere Systeme mit Lager- und Personalwirtschaft kosten 30–100 € pro Monat plus bis zu 1.000 € Hardware. Komplexe Systeme können 100–300 € pro Monat kosten, Hardware 2.000–10.000 €. Bei SDI sind Serviceupdates und Support kostenlos, alle Features ohne versteckte Kosten enthalten, und Du kannst das System als App auf vorhandenen Geräten nutzen.",
            },
            {
              q: "Was ist der Unterschied zwischen einem stationären und einem cloudbasierten Kassensystem?",
              a: "Beim stationären Kassensystem erfolgt die Datenverarbeitung lokal auf einem Gerät. Es ist ohne Internet nutzbar, Updates und Datensicherung müssen jedoch manuell erfolgen. Beim cloudbasierten Kassensystem wie von SDI erfolgt die Datenverarbeitung in der Cloud. Das erfordert Internetzugang, ermöglicht aber automatische Updates, zuverlässige Datensicherung, Echtzeit-Daten und Zugriff von überall – ideal für mobile Einsätze und Filialbetriebe.",
            },
            {
              q: "Brauche ich für mein Kassensystem eine Schulung?",
              a: "Viele Kassensysteme erfordern eine Einschulung. Wir von SDI setzen jedoch auf ein intuitives System mit einfacher Nutzungsoberfläche, die sehr schnell und selbsterklärend erlernt werden kann. Die Einschulung erfolgt entweder kostenfrei mithilfe unserer bereitgestellten Materialien oder mithilfe zubuchbarer Service-Pakete, deren Umfang unsere Kunden selbst wählen.",
            },
            {
              q: "Muss ich Belege ausdrucken oder reicht ein digitaler Bon?",
              a: "In Deutschland gilt seit dem 1. Januar 2020 eine Belegausgabepflicht. Bei jedem Kassenvorgang muss ein Beleg ausgegeben werden – gedruckt oder digital. In bestimmten Fällen kann eine Befreiung beim Finanzamt beantragt werden.",
            },
            {
              q: "Kann ich mein Kassensystem auch für Kartenzahlungen nutzen?",
              a: "Ja, mit SDI können Sie Kartenzahlungen und Mobile Payment problemlos abwickeln – alle gängigen Zahlungsarten wie Visa, Mastercard, American Express, Google Pay und Apple Pay sind direkt integriert, mit nur 1,23 % Gebühr ohne Grundgebühr.",
            },
            {
              q: "Welches Kassensystem eignet sich am besten für meine Branche?",
              a: "Das passende Kassensystem hängt von Ihren individuellen Anforderungen ab. SDI bietet branchenspezifische Lösungen für Einzelhandel, Textil, Gastronomie, Dienstleistung und mehr – wir beraten Sie gerne und empfehlen Ihnen die passende Lösung.",
            },
          ]}
        />
      </Section>
      <ContactForm product="Allgemein" />
    </>
  );
}
